import {
  Command,
  CommandGroup,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { ObjectActionMenuCloseProvider } from "@/object-actions/renderers/ObjectActionMenuCloseContext";
import { ObjectActionMenuItem } from "@/object-actions/renderers/ObjectActionMenuItem";
import type {
  ActionLabelContext,
  ObjectKind,
  ResolvedObjectAction,
} from "@/object-actions/types";
import { groupActionsByCatalogGroup } from "@/object-actions/utils";

export type ObjectActionCommandMenuCursor = {
  screenX: number;
  screenY: number;
};

export interface ObjectActionCommandMenuProps {
  cursorPosition: ObjectActionCommandMenuCursor;
  actions: ResolvedObjectAction[];
  kind: ObjectKind;
  name?: string;
  onClose: () => void;
}

export function ObjectActionCommandMenu({
  cursorPosition,
  actions,
  kind,
  name,
  onClose,
}: ObjectActionCommandMenuProps) {
  const labelCtx: ActionLabelContext = { kind, name };
  const menuActions = actions.filter(
    (a) =>
      a.presentation === "menu-item" ||
      a.presentation === "menu-item-with-shortcut",
  );
  const groups = groupActionsByCatalogGroup(menuActions);

  return (
    <Popover
      defaultOpen
      modal={false}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <PopoverAnchor asChild>
        <div
          aria-hidden
          className="pointer-events-none absolute size-px shrink-0"
          style={{
            left: cursorPosition.screenX,
            top: cursorPosition.screenY,
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-[220px] p-0"
        collisionPadding={8}
        onEscapeKeyDown={(e) => {
          e.stopPropagation();
          onClose();
        }}
        onInteractOutside={() => onClose()}
        side="bottom"
        sideOffset={4}
      >
        <Command>
          <CommandList className="max-h-none overflow-visible">
            <ObjectActionMenuCloseProvider value={onClose}>
              {groups.map((group, groupIndex) => (
                <div key={groupIndex}>
                  {groupIndex > 0 && <CommandSeparator />}
                  <CommandGroup>
                    {group.map((action) => (
                      <ObjectActionMenuItem
                        key={action.id}
                        action={action}
                        labelCtx={labelCtx}
                        variant="command"
                        onSelect={onClose}
                      />
                    ))}
                  </CommandGroup>
                </div>
              ))}
            </ObjectActionMenuCloseProvider>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
